/**
 * FeedbackScreen — Beta feedback form.
 * Collects a structured beta checklist, usability scores, and optional attachments.
 * Wave is injected from auth context — never shown or editable by the user.
 *
 * UI-INV-005: Leaf component, props only, no engine imports.
 */
import { useState, useRef } from "react";
import { useReactMediaRecorder } from "react-media-recorder";
import styles from "./FeedbackScreen.module.css";
import { TEST_IDS } from "../testing/testIds";
import { PrimaryAction } from "../primitives/PrimaryAction";
import { SecondaryAction } from "../primitives/SecondaryAction";
import type { FeedbackData, BetaChecklist } from "../services/feedbackService";

const CONFUSION_MIN_CHARS = 10;
const FALLBACK_ERROR_MSG = "Unable to send feedback. Please check your connection and try again.";

function formatErrorMessage(err: unknown): string {
  if (!(err instanceof Error)) return FALLBACK_ERROR_MSG;

  const msg = err.message.toLowerCase();
  if (msg.includes("not initialized")) return "App initialization incomplete. Please refresh and try again.";
  if (msg.includes("network")) return "Network connection error. Please check your connection.";
  if (msg.includes("cors")) return "Server connection blocked. Please try again in a moment.";
  if (msg.includes("401") || msg.includes("unauthorized")) return "Authentication failed. Please sign in again.";
  if (msg.includes("403") || msg.includes("forbidden")) return "You don't have permission to submit feedback.";
  if (msg.includes("500") || msg.includes("server")) return "Server error. Please try again later.";

  return FALLBACK_ERROR_MSG;
}

type SubmitState = "idle" | "submitting" | "success" | "error";

type Props = {
  userId: string;
  wave: string;
  onSubmit: (data: FeedbackData) => Promise<{ error?: string }>;
  onClose: () => void;
  onSubmitted?: (hasAudio: boolean, hasScreenshots: boolean, hasLog: boolean) => void;
  /** Pre-attach a log file (e.g. from "Report a bug" auto-capture). */
  initialLogFile?: File;
};

function CheckItem({
  checked,
  onChange,
  children,
  indented = false,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: React.ReactNode;
  indented?: boolean;
}) {
  return (
    <label className={indented ? styles.subCheckRow : styles.checkRow}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className={styles.checkbox}
      />
      <span className={styles.checkLabel}>{children}</span>
    </label>
  );
}

function ScoreSlider({
  label,
  value,
  onChange,
  prominent = false,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  prominent?: boolean;
}) {
  return (
    <div className={prominent ? styles.experienceScoreRow : styles.scoreRow}>
      <div className={prominent ? styles.experienceScoreLabel : styles.scoreLabel}>{label}</div>
      <div className={styles.scoreControl}>
        <span className={styles.scoreBound}>1</span>
        <input
          type="range"
          min={1}
          max={10}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className={styles.slider}
        />
        <span className={styles.scoreBound}>10</span>
        <span className={prominent ? styles.experienceScoreValue : styles.scoreValue}>{value}</span>
      </div>
    </div>
  );
}

export function FeedbackScreen({ userId, wave, onSubmit, onClose, onSubmitted, initialLogFile }: Props) {
  // Beta checklist
  const [loginEasy, setLoginEasy] = useState(false);
  const [checkoutEasy, setCheckoutEasy] = useState(false);
  const [foundRetreat, setFoundRetreat] = useState(false);
  const [completedRetreat, setCompletedRetreat] = useState(false);
  const [audioWorked, setAudioWorked] = useState(false);
  const [appStayedOn, setAppStayedOn] = useState(false);
  const [feltOriented, setFeltOriented] = useState(false);
  const [retreatWorthIt, setRetreatWorthIt] = useState(false);
  const [retreatExperienceScore, setRetreatExperienceScore] = useState(7);
  const [wouldDoAgain, setWouldDoAgain] = useState(false);

  // Scores + text
  const [easeScore, setEaseScore] = useState(7);
  const [trustScore, setTrustScore] = useState(7);
  const [confusion, setConfusion] = useState("");
  const [positive, setPositive] = useState("");
  const [notes, setNotes] = useState("");
  const [contactOk, setContactOk] = useState(false);

  // Attachments
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [logFile, setLogFile] = useState<File | null>(initialLogFile ?? null);
  const [uploadedAudioFile, setUploadedAudioFile] = useState<File | null>(null);
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const screenshotInputRef = useRef<HTMLInputElement>(null);
  const logInputRef = useRef<HTMLInputElement>(null);
  const audioUploadRef = useRef<HTMLInputElement>(null);

  const {
    status: recStatus,
    startRecording,
    stopRecording,
    mediaBlobUrl,
    clearBlobUrl,
  } = useReactMediaRecorder({ audio: true });

  const isRecording = recStatus === "recording";
  const hasRecordedAudio = Boolean(mediaBlobUrl);
  const hasUploadedAudio = Boolean(uploadedAudioFile);
  const hasAudio = hasRecordedAudio || hasUploadedAudio;

  function handleScreenshotChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    setScreenshots((prev) => [...prev, ...files]);
    e.target.value = "";
  }

  function handleAudioUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setUploadedAudioFile(file);
    e.target.value = "";
  }

  // Audio alone is sufficient — text field becomes optional when a voice note is attached.
  // Confusion text must have actual content (not just whitespace).
  const hasValidConfusionText = confusion.trim().length >= CONFUSION_MIN_CHARS;
  const canSubmit = (hasAudio || hasValidConfusionText) && submitState !== "submitting";

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitState("submitting");

    let audioBlob: Blob | undefined;
    if (mediaBlobUrl) {
      try {
        const res = await fetch(mediaBlobUrl);
        audioBlob = await res.blob();
      } catch { /* submit without audio */ }
    } else if (uploadedAudioFile) {
      audioBlob = uploadedAudioFile;
    }

    const betaChecklist: BetaChecklist = {
      loginEasy,
      checkoutEasy,
      foundRetreat,
      completedRetreat,
      audioWorked: completedRetreat ? audioWorked : null,
      appStayedOn: completedRetreat ? appStayedOn : null,
      feltOriented: completedRetreat ? feltOriented : null,
      retreatWorthIt: completedRetreat ? retreatWorthIt : null,
      retreatExperienceScore: completedRetreat ? retreatExperienceScore : null,
      wouldDoAgain: completedRetreat ? wouldDoAgain : null,
    };

    try {
      const result = await onSubmit({
        userId,
        wave,
        betaChecklist,
        easeOfUseScore: easeScore,
        trustScore,
        confusionText: confusion.trim(),
        positiveFeedback: positive.trim(),
        additionalNotes: notes.trim(),
        contactPermission: contactOk,
        audioBlob,
        screenshots,
        logFile: logFile ?? undefined,
      });

      if (result.error) {
        setErrorMsg(result.error);
        setSubmitState("error");
      } else {
        onSubmitted?.(Boolean(audioBlob), screenshots.length > 0, Boolean(logFile));
        setSubmitState("success");
      }
    } catch (err) {
      setErrorMsg(formatErrorMessage(err));
      setSubmitState("error");
    }
  }

  if (submitState === "success") {
    return (
      <div className={styles.screen} data-testid={TEST_IDS.FEEDBACK_SCREEN}>
        <div className={styles.successBlock}>
          <div className={styles.successDot} />
          <h2 className={styles.successHeading}>Thank you.</h2>
          <p className={styles.successBody}>
            Your feedback helps shape what Retreat Conductor becomes.
          </p>
          <PrimaryAction onClick={onClose}>Done</PrimaryAction>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.screen} data-testid={TEST_IDS.FEEDBACK_SCREEN}>
      <div className={styles.meta}>
        <button className={styles.backBtn} onClick={onClose} type="button" data-testid={TEST_IDS.BACK_BTN}>
          ← back
        </button>
        <span className={styles.metaLabel}>Beta Feedback</span>
      </div>

      <div className={styles.content}>

        {/* ── How did it go? ── */}
        <section className={styles.section}>
          <div className={styles.sectionLabel}>How did it go?</div>

          <CheckItem checked={loginEasy} onChange={setLoginEasy}>
            I received my sign-in link and logged in without any trouble
          </CheckItem>
          <CheckItem checked={checkoutEasy} onChange={setCheckoutEasy}>
            The checkout and setup was clear — I completed it without issues
          </CheckItem>
          <CheckItem checked={foundRetreat} onChange={setFoundRetreat}>
            I found and chose a retreat on my own — nothing felt hidden or confusing
          </CheckItem>
          <CheckItem checked={completedRetreat} onChange={setCompletedRetreat}>
            I completed at least one retreat (2 minutes or more)
          </CheckItem>

          {completedRetreat && (
            <div className={styles.retreatDetail}>
              <CheckItem checked={audioWorked} onChange={setAudioWorked} indented>
                The bells and audio came through clearly
              </CheckItem>
              <CheckItem checked={appStayedOn} onChange={setAppStayedOn} indented>
                The app stayed on screen — it didn&rsquo;t sleep or close mid-retreat
              </CheckItem>
              <CheckItem checked={feltOriented} onChange={setFeltOriented} indented>
                I felt oriented throughout — I knew where I was in the practice
              </CheckItem>
              <CheckItem checked={retreatWorthIt} onChange={setRetreatWorthIt} indented>
                The retreat felt worth my time
              </CheckItem>

              <ScoreSlider
                label="How was the retreat experience overall?"
                value={retreatExperienceScore}
                onChange={setRetreatExperienceScore}
                prominent
              />

              <CheckItem checked={wouldDoAgain} onChange={setWouldDoAgain} indented>
                I would use Retreat Conductor for another retreat
              </CheckItem>
            </div>
          )}
        </section>

        {/* ── App scores ── */}
        <section className={styles.section}>
          <div className={styles.sectionLabel}>Overall</div>
          <ScoreSlider label="How easy was the app to use?" value={easeScore} onChange={setEaseScore} />
          <ScoreSlider label="How much would you trust this during a real retreat?" value={trustScore} onChange={setTrustScore} />
        </section>

        {/* ── Tell us what you noticed ── */}
        <section className={styles.section}>
          <div className={styles.sectionLabel}>Tell us what you noticed</div>

          {/* Audio — primary option */}
          <div className={styles.audioInvite}>
            <div className={styles.audioInviteLabel}>
              Prefer to just talk? Record a voice note — you don&rsquo;t need to fill in anything else.
            </div>
            {!hasRecordedAudio && !hasUploadedAudio && (
              <div className={styles.evidenceControls}>
                <button
                  type="button"
                  className={isRecording ? styles.recordingBtn : styles.ghostSmBtn}
                  onClick={isRecording ? stopRecording : startRecording}
                >
                  {isRecording ? "■ Stop recording" : "● Record voice note"}
                </button>
                {!isRecording && (
                  <>
                    <span className={styles.divider}>or</span>
                    <button
                      type="button"
                      className={styles.ghostSmBtn}
                      onClick={() => audioUploadRef.current?.click()}
                    >
                      Upload audio file
                    </button>
                    <input
                      ref={audioUploadRef}
                      type="file"
                      accept="audio/*"
                      className={styles.hiddenInput}
                      onChange={handleAudioUpload}
                    />
                  </>
                )}
              </div>
            )}
            {hasRecordedAudio && (
              <div className={styles.evidenceControls}>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <audio src={mediaBlobUrl!} controls className={styles.audioPlayer} />
                <button type="button" className={styles.removeBtn} onClick={clearBlobUrl}>Remove</button>
              </div>
            )}
            {hasUploadedAudio && (
              <div className={styles.attachedFile}>
                {uploadedAudioFile!.name}
                <button type="button" className={styles.removeBtn} onClick={() => setUploadedAudioFile(null)}>×</button>
              </div>
            )}
          </div>

          {/* Text — alternative */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="confusion">
              What was confusing, frustrating, or unclear?
              {!hasAudio && <span className={styles.required}> *</span>}
              {hasAudio && <span className={styles.fieldOptional}> (optional — voice note attached)</span>}
            </label>
            <textarea
              id="confusion"
              className={styles.textarea}
              value={confusion}
              onChange={(e) => setConfusion(e.target.value)}
              placeholder={hasAudio ? "Anything to add in writing?" : "Be specific — this is the most valuable part."}
              rows={4}
            />
            {!hasAudio && confusion.trim().length === 0 && (
              <div className={styles.fieldHint}>
                Need {CONFUSION_MIN_CHARS} characters (or record a voice note above)
              </div>
            )}
            {!hasAudio && confusion.length > 0 && confusion.trim().length < CONFUSION_MIN_CHARS && (
              <div className={styles.fieldHint}>
                {CONFUSION_MIN_CHARS - confusion.trim().length} more characters needed
              </div>
            )}
          </div>
        </section>

        {/* ── Optional ── */}
        <section className={styles.section}>
          <div className={styles.sectionLabel}>Optional</div>

          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="positive">What worked well?</label>
            <textarea
              id="positive"
              className={styles.textarea}
              value={positive}
              onChange={(e) => setPositive(e.target.value)}
              placeholder="Moments that felt right."
              rows={3}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="notes">Anything else?</label>
            <textarea
              id="notes"
              className={styles.textarea}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Feature ideas, context, whatever."
              rows={3}
            />
          </div>

          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={contactOk}
              onChange={(e) => setContactOk(e.target.checked)}
              className={styles.checkbox}
            />
            <span className={styles.checkLabel}>You may contact me for a follow-up conversation.</span>
          </label>
        </section>

        {/* ── Extra evidence ── */}
        <section className={styles.section}>
          <div className={styles.sectionLabel}>Attachments</div>

          <div className={styles.evidenceBlock}>
            <div className={styles.evidenceLabel}>Screenshots</div>
            <div className={styles.evidenceControls}>
              <button type="button" className={styles.ghostSmBtn} onClick={() => screenshotInputRef.current?.click()}>
                + Add images
              </button>
              <input ref={screenshotInputRef} type="file" accept="image/*" multiple className={styles.hiddenInput} onChange={handleScreenshotChange} />
            </div>
            {screenshots.length > 0 && (
              <div className={styles.fileList}>
                {screenshots.map((f, i) => (
                  <div key={i} className={styles.attachedFile}>
                    {f.name}
                    <button type="button" className={styles.removeBtn} onClick={() => setScreenshots((prev) => prev.filter((_, j) => j !== i))}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={styles.evidenceBlock}>
            <div className={styles.evidenceLabel}>Debug log file</div>
            <div className={styles.evidenceControls}>
              <button type="button" className={styles.ghostSmBtn} onClick={() => logInputRef.current?.click()}>
                Attach log file
              </button>
              <input
                ref={logInputRef}
                type="file"
                accept=".json,.ndjson,.txt"
                className={styles.hiddenInput}
                onChange={(e) => { setLogFile(e.target.files?.[0] ?? null); e.target.value = ""; }}
              />
            </div>
            {logFile && (
              <div className={styles.attachedFile}>
                {logFile.name}
                <button type="button" className={styles.removeBtn} onClick={() => setLogFile(null)}>×</button>
              </div>
            )}
          </div>
        </section>

        {submitState === "error" && (
          <p className={styles.errorMsg}>Submission failed: {errorMsg}. Please try again.</p>
        )}

        <div className={styles.actions}>
          <PrimaryAction onClick={handleSubmit} disabled={!canSubmit} testId={TEST_IDS.FEEDBACK_SUBMIT_BTN}>
            {submitState === "submitting" ? "Sending…" : "Send Feedback"}
          </PrimaryAction>
          <SecondaryAction onClick={onClose} testId={TEST_IDS.FEEDBACK_CANCEL_BTN}>Cancel</SecondaryAction>
        </div>
      </div>
    </div>
  );
}
